import React, { useCallback, useEffect, useState } from "react";
import { getCourseMessages, getUnreadCourses } from "../../api/academic";
import CourseMessagesBlock from "./CourseMessagesBlock";

/** Drawer global de mensajes: desde el icono del header. Primero lista de cursos; al elegir uno, conversación. */
export default function GlobalChatDrawer({
	open,
	onClose,
	courses = [],
	onUnreadChange,
	emptyCoursesMessage = "No tienes cursos matriculados.",
}) {
	const [selectedRunId, setSelectedRunId] = useState(null);
	const [threads, setThreads] = useState([]);
	const [selectedStudentId, setSelectedStudentId] = useState(null);
	const [isTeacherMode, setIsTeacherMode] = useState(false);
	const [loading, setLoading] = useState(false);
	const [unreadRuns, setUnreadRuns] = useState([]);

	useEffect(() => {
		if (!open || selectedRunId) return;
		getUnreadCourses()
			.then(data => setUnreadRuns(data))
			.catch(() => setUnreadRuns([]));
	}, [open, selectedRunId]);

	const loadThreads = useCallback(async (clearSelection = false, silent = false) => {
		if (!selectedRunId || !open) return;
		if (!silent) setLoading(true);
		if (clearSelection) setSelectedStudentId(null);
		try {
			const data = await getCourseMessages(selectedRunId);
			if (Array.isArray(data.threads)) {
				setIsTeacherMode(true);
				setThreads(data.threads || []);
			} else {
				setIsTeacherMode(false);
				setThreads([]);
			}
		} catch {
			setIsTeacherMode(false);
			setThreads([]);
		} finally {
			if (!silent) setLoading(false);
		}
	}, [selectedRunId, open]);

	useEffect(() => {
		loadThreads(true, false);
	}, [loadThreads]);

	useEffect(() => {
		if (!open || !selectedRunId) return;
		const interval = setInterval(() => loadThreads(false, true), 3000);
		return () => clearInterval(interval);
	}, [open, selectedRunId, loadThreads]);

	useEffect(() => {
		if (!open) return;
		const onKeyDown = (e) => {
			if (e.key === "Escape") {
				if (selectedStudentId) setSelectedStudentId(null);
				else if (selectedRunId) setSelectedRunId(null);
				else onClose();
			}
		};
		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [open, onClose, selectedRunId, selectedStudentId]);
	// Al abrir el hilo, el backend marca como leídos; avisar para actualizar badge
	useEffect(() => {
		if (open && selectedRunId && onUnreadChange) onUnreadChange();
	}, [open, selectedRunId, onUnreadChange]);

	const handleFullClose = () => {
		setSelectedRunId(null);
		setSelectedStudentId(null);
		onClose();
	};

	const handleBack = () => {
		if (selectedStudentId) {
			setSelectedStudentId(null);
		} else if (selectedRunId) {
			setSelectedRunId(null);
			if (onUnreadChange) onUnreadChange();
		}
	};

	const selectedRun = courses.find((c) => c.id === selectedRunId);
	const courseName = selectedRun
		? (selectedRun.code && selectedRun.name ? `${selectedRun.code} – ${selectedRun.name}` : selectedRun.name || selectedRun.code || "Curso")
		: "";

	if (!open) return null;

	return (
		<>
			<div
				className={`course-messages-drawer-backdrop ${open ? "open" : ""}`}
				onClick={handleFullClose}
				aria-hidden="true"
			/>
			<aside
				className={`course-messages-drawer ${open ? "open" : ""}`}
				aria-label="Panel global de mensajes"
				role="dialog"
				aria-modal="true"
			>
				<div className="course-messages-drawer-inner">
					<header className="course-messages-drawer-header">
						{selectedRunId ? (
							<button
								type="button"
								className="course-messages-drawer-back"
								onClick={handleBack}
								aria-label="Volver"
								style={{ border: 'none', background: 'none', marginRight: '10px', cursor: 'pointer' }}
							>
								<i className="ri-arrow-left-line" aria-hidden />
							</button>
						) : (
							<span className="course-messages-drawer-header-spacer" aria-hidden />
						)}
						<h2 className="course-messages-drawer-title course-messages-drawer-title--center">
							{selectedStudentId ? "Conversación" : selectedRunId ? (courseName || "Mensajes") : "Tus Cursos"}
						</h2>
						<button
							type="button"
							className="course-messages-drawer-close"
							onClick={handleFullClose}
							aria-label="Cerrar panel de mensajes"
						>
							<i className="ri-close-line" aria-hidden />
						</button>
					</header>

					{/* CONTENEDOR PRINCIPAL FLUIDO */}
					<div className="course-messages-drawer-body" style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

						{!selectedRunId ? (
							<div className="course-messages-drawer-contacts" style={{ flex: 1, overflowY: 'auto' }}>
								<div className="course-messages-drawer-contacts-head">
									<span>Elige un curso para ver los mensajes</span>
								</div>
								<div className="course-messages-drawer-category-content">
									{courses.length === 0 ? (
										<p className="course-messages-drawer-empty ">{emptyCoursesMessage}</p>
									) : (
										courses.map((run) => {
											// AQUÍ ESTÁ LA LÍNEA QUE FALTABA: Calculamos si este curso tiene mensajes sin leer
											const hasUnread = unreadRuns.includes(run.id);

											return (
												<button
													key={run.id}
													type="button"
													className="course-messages-drawer-contact-item mb-2"
													onClick={() => setSelectedRunId(run.id)}
												>
													<span className="course-messages-drawer-contact-avatar">
														<i className="ri-book-2-line"></i>
													</span>
													<span className="course-messages-drawer-contact-name" style={{ flex: 1 }}>
														{run.name || run.code || `Curso ${run.id}`}
													</span>

													{/* AHORA SÍ SABE QUÉ ES hasUnread */}
													{hasUnread && (
														<span
															className="bg-danger rounded-circle ms-auto me-2 shadow-sm"
															style={{ width: '10px', height: '10px', display: 'inline-block' }}
															title="Nuevo mensaje en este curso"
														></span>
													)}
													<i className="ri-arrow-right-s-line course-messages-drawer-contact-arrow" aria-hidden />
												</button>
											);
										})
									)}
								</div>
							</div>
						) : !selectedStudentId ? (
							<div className="course-messages-drawer-contacts" style={{ flex: 1, overflowY: 'auto' }}>
								<div className="course-messages-drawer-categories">
									<div className="course-messages-drawer-category open">
										<div className="course-messages-drawer-category-content">
											{loading ? (
												<p className="course-messages-drawer-loading">Cargando...</p>
											) : isTeacherMode ? (
												threads.length === 0 ? (
													<p className="course-messages-drawer-empty">Aún no hay conversaciones con estudiantes.</p>
												) : (
													threads.map((t) => (
														<button
															key={t.student_id}
															type="button"
															className={`course-messages-drawer-contact-item ${selectedStudentId === t.student_id ? "selected" : ""}`}
															onClick={() => setSelectedStudentId(t.student_id)}
															aria-current={selectedStudentId === t.student_id}
														>
															<span className="course-messages-drawer-contact-avatar">
																<i className="ri-user-line" aria-hidden />
															</span>
															<span className="course-messages-drawer-contact-name" style={{ flex: 1 }}>{t.student_name}</span>
															{t.has_unread && (
																<span
																	className="bg-danger rounded-circle ms-auto me-2 shadow-sm"
																	style={{ width: '10px', height: '10px', display: 'inline-block' }}
																	title="Nuevo mensaje"
																></span>
															)}
															<i className="ri-arrow-right-s-line course-messages-drawer-contact-arrow" aria-hidden />
														</button>
													))
												)
											) : (
												<button type="button" className="course-messages-drawer-contact-item selected" onClick={() => setSelectedStudentId('teacher')}>
													<span className="course-messages-drawer-contact-avatar">
														<i className="ri-user-2-line" aria-hidden />
													</span>
													<span className="course-messages-drawer-contact-name" style={{ flex: 1 }}>Conversación con el docente</span>
													<i className="ri-arrow-right-s-line course-messages-drawer-contact-arrow" aria-hidden />
												</button>
											)}
										</div>
									</div>
								</div>
							</div>
						) : (
							<div className="course-messages-drawer-chat" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
								<div className="selected-user-header">
									{(() => {
										const student = threads.find(t => t.student_id === selectedStudentId);
										return (
											<div className="course-messages-drawer-contact-item selected" style={{ cursor: 'default' }}>
												<span className="course-messages-drawer-contact-avatar">
													<i className="ri-user-line" />
												</span>
												<span className="course-messages-drawer-contact-name" style={{ flex: 1 }}>
													{isTeacherMode ? (student?.student_name || "Alumno") : "Docente del curso"}
												</span>
											</div>
										);
									})()}
								</div>

								<div className="alert alert-warning m-2 p-2 text-center" style={{ fontSize: '0.8rem' }}>
									<i className="ri-error-warning-line me-1"></i>
									Por seguridad, no compartas contraseñas ni datos sensibles por este medio.
								</div>

								<div style={{ flex: 1, overflowY: 'auto', padding: '10px' }}>
									<CourseMessagesBlock
										runId={selectedRunId}
										recipientId={selectedStudentId === 'teacher' ? null : selectedStudentId}
										title=""
										variant="drawer"
										onSendOrLoad={onUnreadChange}
										isDrawerOpen={open}
									/>
								</div>
							</div>
						)}
					</div>
				</div>
			</aside>
		</>
	);
}